<template>
    <div class="space-y-6 pb-8">
        <h1 class="-mb-4 pl-2 text-2xl font-semibold uppercase tracking-wide">
            {{ planTitle }}
        </h1>

        <plan-presentation
            v-if="!isEditing"
            :plan="planData"
            :allow-bookings="allowBookings"
        />

        <template v-if="allowBookings">
            <duplicate-contract-message
                :plan-id="planData.id"
                :contract-id="contractData?.id"
                :warning-text="t('purchaseContract.duplicateGiftCardWarning')"
                :edit-text="t('purchaseContract.editGiftCard')"
            />

            <select-gift-card
                :is-active-section="activeSection === 0"
                :contract="contractData"
                :plan="planData"
                :is-mutating="isCreatingContract || isChangingContract"
                @create-contract="handleCreateContract"
                @change-contract="handleChangeContract"
                @edit-section="activeSection = 0"
            />

            <template v-if="contractData !== undefined">
                <document-section
                    v-if="activeSection > 0"
                    :contract="contractData"
                    :is-signing-document="isSigningDocument"
                    :invalid-initials="invalidInitials"
                    :invalid-signatures="invalidSignatures"
                    @add-to-cart="addToCart"
                    @sign-document="handleSignDocument"
                />
            </template>
        </template>
    </div>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const PurchaseGiftCardPlanFragment = graphql(/** GraphQL */ `
    fragment PurchaseGiftCardPlanFragment on Plan {
        id
        planType
        name
        ...PlanPresentationPlanFragment
        ...SelectGiftCardPlanFragment
        presentation(input: $input, language: $language) {
            title
        }
        checkAllowBookings: bookingLimit(input: $input) {
            allowBookings
        }
    }
`)

export const PurchaseGiftCardContractFragment = graphql(/** GraphQL */ `
    fragment PurchaseGiftCardContractFragment on PlanContract {
        ...SelectGiftCardContractFragment
        ...DocumentSectionContractFragment
        ...PaymentMethodInputPlanContractFragment
        id
        draftExpiresAtUtc
    }
`)
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import SelectGiftCard from '~/components/PurchaseContract/GiftCard/SelectGiftCard.vue'
import DocumentSection from '~/components/PurchaseContract/DocumentSection/DocumentSection.vue'
import type {
    ChangeContractInput,
    CreateGiftCardInput,
    PurchaseGiftCardContractFragmentFragment,
    SignDocumentInput,
    UpdateContractInput
} from '~/resources/graphql/graphql'
import type { FragmentType } from '~/resources/graphql'
import { getFragmentData } from '~/resources/graphql'
import {
    setCartVisible,
    useRedirectOnContractDelete,
    useSetCartData
} from '~/composables/use-cart'
import { useRouter } from 'vue-router'
import DuplicateContractMessage from '~/components/PurchaseContract/DuplicateContractMessage.vue'
import { useRouteLink } from '~/composables/routing'
import PlanPresentation from '~/components/PurchaseContract/PlanPresentation.vue'
import { parseUtc } from '~/composables/support/localization'
import { isAfter } from 'date-fns'
import { useGiftCardCreateContractMutation } from '~/components/PurchaseContract/GiftCard/use-gift-card-create-contract-mutation'
import { useGiftCardChangeContractMutation } from '~/components/PurchaseContract/GiftCard/use-gift-card-change-contract.mutation'
import { useGiftCardUpdateContractMutation } from '~/components/PurchaseContract/GiftCard/use-gift-card-update-contract-mutation'
import { useGiftCardSignDocumentMutation } from '~/components/PurchaseContract/GiftCard/use-gift-card-sign-document-mutation'
import { useStorefrontPrefix } from '~/composables/use-storefront-prefix'
import { useOrgShortTitle } from '~/composables/use-org'
import { useHead } from '@unhead/vue'
import { handleValidationErrors } from '~/components/PurchaseContract/DocumentSection/handle-validation-errors'
import { useI18n } from 'vue-i18n'

const props = withDefaults(
    defineProps<{
        plan: FragmentType<typeof PurchaseGiftCardPlanFragment>
        initialContract?: FragmentType<typeof PurchaseGiftCardContractFragment>
    }>(),
    {
        initialContract: undefined
    }
)

const isEditing = computed(() => props.initialContract !== undefined)
const { t } = useI18n({ useScope: 'global' })

const planData = computed(() =>
    getFragmentData(PurchaseGiftCardPlanFragment, props.plan)
)

const planTitle = computed(() => {
    if (
        planData.value.presentation !== null &&
        planData.value.presentation.title !== ''
    ) {
        return planData.value.presentation.title
    }

    return planData.value.name
})

const shortTitle = useOrgShortTitle()
useHead({
    title: () => shortTitle.value + ' | ' + planTitle.value
})

const allowBookings = computed(
    () => planData.value.checkAllowBookings.allowBookings
)

const contractData = ref<PurchaseGiftCardContractFragmentFragment | undefined>(
    props.initialContract !== undefined
        ? getFragmentData(
              PurchaseGiftCardContractFragment,
              props.initialContract
          )
        : undefined
)

const activeSection = ref(0)
function setEditingActiveSection() {
    // If we are not editing, return
    if (contractData.value === undefined) {
        return
    }

    // If the contract we are editing has expired, start from beginning
    if (contractData.value.draftExpiresAtUtc !== null) {
        const expiresAtUtc = parseUtc(contractData.value.draftExpiresAtUtc)
        if (isAfter(new Date(), expiresAtUtc)) {
            activeSection.value = 0
            return
        }
    }

    // There's only 2 sections, so start at the second one
    activeSection.value = 1
}
// Run our function on setup
setEditingActiveSection()

const setCartData = useSetCartData()
const { mutate: createGiftCard, isPending: isCreatingContract } =
    useGiftCardCreateContractMutation()
function handleCreateContract(input: CreateGiftCardInput) {
    createGiftCard(input, {
        onSuccess: data => {
            contractData.value = getFragmentData(
                PurchaseGiftCardContractFragment,
                data
            )
            activeSection.value = 1
        }
    })
}

const { mutate: changeContract, isPending: isChangingContract } =
    useGiftCardChangeContractMutation()
function handleChangeContract(input: ChangeContractInput) {
    changeContract(input, {
        onSuccess: data => {
            if (data.changeContract.__typename === 'ChangeContractResult') {
                // Update the cart data
                if (data.changeContract.query.viewer !== null) {
                    setCartData(data.changeContract.query.viewer.cart)
                }
                if (data.changeContract.record.__typename === 'PlanContract') {
                    contractData.value = getFragmentData(
                        PurchaseGiftCardContractFragment,
                        data.changeContract.record
                    )
                    activeSection.value = 1
                    return
                }
            }

            throw new Error()
        }
    })
}

const { mutate: updateContract, isPending: isUpdatingContract } =
    useGiftCardUpdateContractMutation()
function handleContinue(mutationData: UpdateContractInput) {
    updateContract(mutationData, {
        onSuccess: data => {
            if (data.updateContract.__typename === 'UpdateContractResult') {
                // Update the cart data
                if (data.updateContract.query.viewer !== null) {
                    setCartData(data.updateContract.query.viewer.cart)
                }
                if (data.updateContract.record.__typename === 'PlanContract') {
                    contractData.value = getFragmentData(
                        PurchaseGiftCardContractFragment,
                        data.updateContract.record
                    )
                    activeSection.value = 2
                    return
                }
            }

            throw new Error()
        }
    })
}

function handleContinueNoDocument(input: UpdateContractInput) {
    updateContract(input, {
        onSuccess: data => {
            if (data.updateContract.__typename === 'UpdateContractResult') {
                // Update the cart data
                if (data.updateContract.query.viewer !== null) {
                    setCartData(data.updateContract.query.viewer.cart)
                }

                addToCart()
                return
            }

            throw new Error()
        }
    })
}

const invalidInitials = ref(new Set<number>())
const invalidSignatures = ref(new Set<number>())

const { mutate: signDocument, isPending: isSigningDocument } =
    useGiftCardSignDocumentMutation()
function handleSignDocument(mutationData: SignDocumentInput) {
    signDocument(mutationData, {
        onSuccess: data => {
            if (data.signDocument.__typename === 'ValidationError') {
                handleValidationErrors(
                    data.signDocument.fieldErrors,
                    invalidInitials,
                    invalidSignatures
                )
                return
            }

            if (data.signDocument.__typename === 'SignDocumentResult') {
                // Update the cart data
                if (data.signDocument.query.viewer !== null) {
                    setCartData(data.signDocument.query.viewer.cart)
                }

                if (data.signDocument.record.__typename === 'PlanContract') {
                    contractData.value = getFragmentData(
                        PurchaseGiftCardContractFragment,
                        data.signDocument.record
                    )
                    return
                }
            }

            throw new Error()
        }
    })
}

const router = useRouter()
const storefrontPrefix = useStorefrontPrefix()
const { storefrontHomeLink } = useRouteLink()
function addToCart() {
    setCartVisible(true)
    router.push(storefrontHomeLink(storefrontPrefix.value))
}

watch(
    () => planData.value.id,
    (newValue, oldValue) => {
        if (newValue !== oldValue) {
            // close all the components
            activeSection.value = 0
            // Remove contract data
            contractData.value = undefined
        }
    }
)

useRedirectOnContractDelete(() => contractData.value?.id)
</script>
